import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, Target } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { TargetTable } from './TargetTable';
import { SalaryTab } from './SalaryTab';
import { HistoryList } from './HistoryList';
import { MultiplierGrid } from './MultiplierGrid';
import { TopTrainer } from './TopTrainer';
import { inr } from './fmt';

const Kpi = ({ label, value, sub, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </Card>
);

const DefaultMultiple = ({ quarter, value, onSaved }) => {
  const [v, setV] = useState(value);
  const [note, setNote] = useState('');
  useEffect(() => setV(value), [value]);
  const save = async () => {
    try {
      await api.put(`/trainer-targets/quarter/${quarter}/multiple`, { default_multiple: v, note });
      toast.success('Default multiple updated'); setNote(''); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="tt-default-multiple">
      <label className="text-xs text-slate-600">Default multiple
        <Input type="number" step="0.1" value={v} onChange={e => setV(e.target.value)} className="h-8 w-24 text-xs mt-1" data-testid="tt-default-multiple-input" />
      </label>
      <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Reason for change" className="h-8 w-56 text-xs" data-testid="tt-default-multiple-note" />
      <Button size="sm" className="h-8" disabled={String(v) === String(value) || !note.trim()} onClick={save} data-testid="tt-default-multiple-save">Save</Button>
    </div>
  );
};

export default function TrainerTargetPage() {
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get('/trainer-targets/quarters').then(r => { setMeta(r.data); setQ(r.data.current); }); }, []);
  const load = useCallback(() => { if (q) api.get(`/trainer-targets/quarter/${q}`).then(r => setD(r.data)); setTick(t => t + 1); }, [q]);
  useEffect(() => { setD(null); load(); }, [load]);
  if (!meta) return <div className="py-20 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const t = d?.totals || {};
  return (
    <div className="space-y-5" data-testid="trainer-target-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><Target className="w-5 h-5 text-violet-600" />Trainer Target</h1>
          <p className="text-sm text-slate-500">Quarterly target = monthly salary of each month × multiple. Achievement counts fees of students in batches that started in the quarter.</p>
        </div>
        <select value={q} onChange={e => setQ(e.target.value)} className="h-9 border border-slate-200 rounded-md text-sm px-2 bg-white" data-testid="tt-quarter-select">
          {meta.quarters.map(x => <option key={x.key} value={x.key}>{x.label}{x.key === meta.current ? ' (current)' : ''}</option>)}
        </select>
      </div>
      <Tabs defaultValue="targets">
        <TabsList>
          <TabsTrigger value="targets" data-testid="tt-tab-targets">Targets</TabsTrigger>
          {meta.can_see_salary && <TabsTrigger value="salary" data-testid="tt-tab-salary">Salaries</TabsTrigger>}
          <TabsTrigger value="history" data-testid="tt-tab-history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="targets" className="space-y-4">
          {!d ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi label="Team target" value={inr(t.target)} sub={`${d.rows.filter(r => r.target).length} of ${d.rows.length} trainers have a target`} testid="tt-kpi-target" />
              <Kpi label="Billed" value={inr(t.billed)} sub={t.target ? `${Math.round(t.billed_with_target / t.target * 100)}% of target (trainers with a target)` : 'No targets set yet'} testid="tt-kpi-billed" />
              <Kpi label="Collected" value={inr(t.collected)} sub={t.target ? `${Math.round(t.collected_with_target / t.target * 100)}% of target (trainers with a target)` : 'No targets set yet'} testid="tt-kpi-collected" />
              <Kpi label="Quarter" value={d.quarter.label} sub={`${d.quarter.months[0]} → ${d.quarter.months[2]}`} testid="tt-kpi-quarter" />
            </div>
            <TopTrainer top={d.top} quarter={d.quarter.label} />
            <DefaultMultiple quarter={q} value={d.default_multiple} onSaved={load} />
            <TargetTable d={d} quarter={q} onSaved={load} isAdmin={meta.can_see_salary} />
          </>}
        </TabsContent>
        {meta.can_see_salary && <TabsContent value="salary"><SalaryTab source={meta.salary_source} onSaved={load} /></TabsContent>}
        <TabsContent value="history" className="space-y-4"><MultiplierGrid tick={tick} /><HistoryList quarter={q} tick={tick} isAdmin={meta.can_see_salary} /></TabsContent>
      </Tabs>
    </div>
  );
}
